import { useCallback, useState } from "react";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  fetchAdminDashboardSnapshot,
  fetchSnowMapSessions,
  type AdminDashboardSnapshot,
  type SnowMapSessionsResult,
  DASHBOARD_ACTIVITY_PAGE_SIZE,
} from "@/hooks/adminDashboardFetch";
import type { RecentActivityItem } from "@/components/admin/AdminDashboardActivity";

export const adminDashboardQueryKey = (
  orgId: string | null | undefined,
  showArchived: boolean
) => ["admin-dashboard", orgId ?? "", showArchived] as const;

export const orgCommandCenterSettingsKey = (orgId: string | null | undefined) =>
  ["org-command-center-settings", orgId ?? ""] as const;

export type OrgCommandCenterSettings = {
  globalCoordinatorViewFinancials: boolean;
  isSnowSeason: boolean;
  taskGenerationDays: number;
  autoCheckoutDistanceMeters: number;
};

/** Org-level switches — must not share a query key with "show archived". */
export function useOrgCommandCenterSettings(
  currentOrgId: string | null | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: orgCommandCenterSettingsKey(currentOrgId),
    queryFn: async (): Promise<OrgCommandCenterSettings> => {
      const { data, error } = await supabase
        .from("organizations")
        .select("global_coordinator_view_financials, is_snow_season, task_generation_days, auto_checkout_distance_meters")
        .eq("id", currentOrgId!)
        .single();
      if (error) throw error;
      return {
        globalCoordinatorViewFinancials: data.global_coordinator_view_financials === true,
        isSnowSeason: data.is_snow_season === true,
        taskGenerationDays:
          typeof data.task_generation_days === "number" && data.task_generation_days > 0
            ? data.task_generation_days
            : 15,
        autoCheckoutDistanceMeters:
          typeof data.auto_checkout_distance_meters === "number" && data.auto_checkout_distance_meters > 0
            ? data.auto_checkout_distance_meters
            : 400,
      };
    },
    enabled: Boolean(currentOrgId) && enabled,
    staleTime: 60_000,
  });
}

/**
 * Cached dashboard payload (TanStack Query) — same pattern as Domio-Serwis manager hooks.
 * Survives route changes (e.g. property details → back) without empty flash.
 */
export function useAdminDashboardData(
  currentOrgId: string | null | undefined,
  isRoleLoading: boolean,
  showArchivedProperties: boolean
) {
  const queryClient = useQueryClient();
  const [isLoadingMoreActivity, setIsLoadingMoreActivity] = useState(false);

  const query = useQuery({
    queryKey: adminDashboardQueryKey(currentOrgId, showArchivedProperties),
    queryFn: () => fetchAdminDashboardSnapshot(currentOrgId!, showArchivedProperties),
    enabled: Boolean(currentOrgId) && !isRoleLoading,
    staleTime: 30_000,
    gcTime: 10 * 60_000,
    placeholderData: keepPreviousData,
  });

  const loadMoreActivity = useCallback(async () => {
    if (!currentOrgId) return;
    const key = adminDashboardQueryKey(currentOrgId, showArchivedProperties);
    const snapshot = queryClient.getQueryData<AdminDashboardSnapshot>(key);
    if (!snapshot?.activityHasMore || isLoadingMoreActivity) return;

    setIsLoadingMoreActivity(true);
    const nextPage = snapshot.activityPage + 1;
    const from = nextPage * DASHBOARD_ACTIVITY_PAGE_SIZE;
    const to = from + DASHBOARD_ACTIVITY_PAGE_SIZE - 1;
    try {
      const { data: activityLogsRes } = await supabase
        .from("task_step_logs")
        .select(
          "id, user_id, step_name, created_at, cleaning_tasks!inner(location_id, org_id)"
        )
        .eq("cleaning_tasks.org_id", currentOrgId)
        .order("created_at", { ascending: false })
        .range(from, to);
      const logs = (activityLogsRes ?? []) as {
        id: string;
        user_id: string;
        step_name: string;
        created_at: string;
        cleaning_tasks: { location_id: string | null; org_id: string } | null;
      }[];
      if (logs.length === 0) {
        queryClient.setQueryData<AdminDashboardSnapshot>(key, (prev) =>
          prev ? { ...prev, activityHasMore: false } : prev
        );
        return;
      }
      const userIds = [...new Set(logs.map((l) => l.user_id).filter(Boolean))];
      const { data: prof } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);
      const profilesList = (prof ?? []) as { id: string; full_name: string | null }[];
      const locMap = new Map(snapshot.mapLocations.map((l) => [l.id, l.address]));
      const newItems: RecentActivityItem[] = logs.map((log) => {
        const task = log.cleaning_tasks;
        const locationId = task?.location_id ?? null;
        const addr = locationId ? locMap.get(locationId) ?? "?" : "?";
        const p = profilesList.find((x) => x.id === log.user_id);
        return {
          id: log.id,
          completed_at: log.created_at,
          staff_name: p?.full_name ?? null,
          staff_id: log.user_id,
          location_address: addr,
          location_id: locationId ?? "",
          step_name: log.step_name,
        };
      });
      queryClient.setQueryData<AdminDashboardSnapshot>(key, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          activity: [...prev.activity, ...newItems],
          activityPage: nextPage,
          activityHasMore: logs.length >= DASHBOARD_ACTIVITY_PAGE_SIZE,
        };
      });
    } catch (e) {
      console.error("AdminDashboard loadMoreActivity:", e);
    } finally {
      setIsLoadingMoreActivity(false);
    }
  }, [
    currentOrgId,
    showArchivedProperties,
    queryClient,
    isLoadingMoreActivity,
  ]);

  return {
    ...query,
    loadMoreActivity,
    isLoadingMoreActivity,
  };
}

export const snowMapSessionsQueryKey = (
  orgId: string | null | undefined,
  cutoffIso: string
) => ["snow-map-sessions", orgId ?? "", cutoffIso] as const;

export function useSnowMapSessions(
  orgId: string | null | undefined,
  cutoff: Date,
  enabled: boolean
) {
  const cutoffIso = cutoff.toISOString();
  return useQuery<SnowMapSessionsResult>({
    queryKey: snowMapSessionsQueryKey(orgId, cutoffIso),
    queryFn: () => fetchSnowMapSessions(orgId!, cutoff),
    enabled: Boolean(orgId) && enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}
